import { Link } from "react-router-dom";
import { SITE_URL } from "@/config/site";
import { useLocalePrefix } from "@/hooks/useLocalePrefix";
import { deepWithStats } from "@/data/modelStats";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Sparkles,
  ClipboardList,
  Users,
  GraduationCap,
  Megaphone,
  Briefcase,
  Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

// Icon lookup by name (kits.list[].icon in i18n stores a lucide icon name).
const ICONS = {
  ClipboardList,
  Users,
  GraduationCap,
  Megaphone,
  Briefcase,
  Rocket,
};

// Public index of the profession Use-Case Kits, per-locale (/kits, /:lng/kits).
// Content is i18n-driven (kits.* namespace, 15 languages). Prerendered by
// scripts/prerender-seo.js + listed in sitemap.xml. §7: the market buys AI by
// profession.
const KitsPage = () => {
  const { t } = useTranslation();
  // `lng` present on /:lng/kits, absent on the FR root /kits — keeps links
  // within the active locale.
  const localePrefix = useLocalePrefix();
  const rawKits = deepWithStats(t("kits.list", { returnObjects: true }));
  const kits = Array.isArray(rawKits) ? rawKits : [];

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("kits.seoIndexTitle"),
    itemListElement: kits.map((k, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${localePrefix}/kits/${k.slug}`,
      name: k.title,
    })),
  };

  return (
    <PageLayout>
      <SEO
        title={t("kits.seoIndexTitle")}
        description={t("kits.seoIndexMeta")}
        canonical="/kits"
        jsonLd={itemList}
      />
      <section className="section-y-sm px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-primary text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" /> {t("kits.badge")}
            </span>
            <h1 className="animate-in-up font-heading text-fluid-h1 font-bold text-white mb-4">
              {t("kits.h1")}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {t("kits.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {kits.map((k) => {
              const Icon = ICONS[k.icon] || Sparkles;
              return (
                <Link
                  key={k.slug}
                  to={`${localePrefix}/kits/${k.slug}`}
                  className="glass lift rounded-2xl p-6 hover:border-primary/40 group block"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/25 to-accent/15 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                    {k.title}
                  </h2>
                  <p className="text-slate-400 text-sm mb-4">{k.tagline}</p>
                  <span className="text-xs text-slate-500 mb-4 block">
                    {t("kits.promptsCount", { count: k.templates.length })}
                  </span>
                  <span className="text-primary text-sm flex items-center gap-1 font-medium">
                    {t("kits.cardCta")} <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-16">
            <p className="text-slate-400 mb-6">
              {t("kits.ctaNote")}{" "}
              <Link
                to={`${localePrefix}/pricing`}
                className="text-primary underline underline-offset-2"
              >
                {t("kits.seePricing")}
              </Link>
              .
            </p>
            <Link to="/register">
              <Button
                size="lg"
                className="btn-glow bg-primary-strong hover:bg-primary-strong/90 text-lg px-8"
              >
                {t("kits.ctaBtn")} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default KitsPage;
